import PptxGenJS from "pptxgenjs"
import { splitTextInRect } from 'shared/util'

import { slideFrom } from '../util/slideFrom'
import { KojinPptxComponentsProps } from '../types'

import { continueText } from './continueText'
import { ArticleResult } from "interface/article"
import { getJournalDate } from "features/article/util"

const journalTableLayout = {
  column: {
    number: 0.6,
    //title,
    journal: 4,
    date: 1
  },
  margin: {
    cx: 0.1,
    cy: 0.05
  }
}
const colors = {
  border: 'ffffff',
  header: 'bcbdc0',
  even: 'e7e8e8',
  odd: 'f4f4f4'
}

type journalTableProps = KojinPptxComponentsProps & {
  articles: ArticleResult[]
  scale?: number
  top: number
}
type journalTableReturnValue = {
	slide: PptxGenJS.Slide
  bottom: number
}
export const journalTable = ({
  articles,
  scale: nullableScale,
  top,
  ...other
}: journalTableProps): journalTableReturnValue => {
  const { pptx, layout, slide } = other
  if (articles.length === 0) return { slide, bottom: top }

  const lineHeight = 0.017 * layout.baseFont.fontSize
  const scale = nullableScale ?? 1

  const header: PptxGenJS.TableRow[] = [[{
    text: 'No',
    options: {
      ...layout.cellOption,
      fontSize: (layout.cellOption.fontSize ?? 0) * scale,
      fill: { color: colors.header },
      align: 'center'
    }
  }, {
    text: 'タイトル',
    options: {
      ...layout.cellOption,
      fontSize: (layout.cellOption.fontSize ?? 0) * scale,
      fill: { color: colors.header }
    }
  }, {
    text: '掲載誌',
    options: {
      ...layout.cellOption,
      fontSize: (layout.cellOption.fontSize ?? 0) * scale,
      fill: { color: colors.header }
    }
  }, {
    text: '発表年',
    options: {
      ...layout.cellOption,
      fontSize: (layout.cellOption.fontSize ?? 0) * scale,
      fill: { color: colors.header },
      align: 'center'
    }
  }]]
  const bottom = slideFrom(layout, 'bottom', 0)
  const titleWidth = slideFrom(layout, 'width', 
    (journalTableLayout.column.number * scale) + 
    (journalTableLayout.column.journal * scale) + 
    (journalTableLayout.column.date * scale))

  let lastSlide = slide
  let tableTop = top + layout.margin
  let at = tableTop + ((journalTableLayout.margin.cy + (lineHeight * scale) + journalTableLayout.margin.cy) * header.length)
  let i = 0
  let body: PptxGenJS.TableRow[] = []
  for(const article of articles) {
    const titleJpnLines = splitTextInRect((article.titleJpn ?? '').replace(/\n/g, ''),
      layout.baseFont.fontSize * scale,
      layout.baseFont.fontFace,
      (titleWidth - (journalTableLayout.margin.cx + journalTableLayout.margin.cx)) * layout.inchPerDot
    ) || []

    const titleEngLines = (article.titleJpn !== article.title) ? splitTextInRect((article.title ?? '').replace(/\n/g, ''),
      layout.baseFont.fontSize * scale,
      layout.baseFont.fontFace,
      (titleWidth - (journalTableLayout.margin.cx + journalTableLayout.margin.cx)) * layout.inchPerDot
    ) || [] : []

    const journalLines = splitTextInRect([
      article.journal.title ?? [],
      article.journal.issue ?? [],
      article.pagination ?? []
    ].flat().join(' ').replace(/\n/g, ''),
      layout.baseFont.fontSize * scale,
      layout.baseFont.fontFace, 
      ((journalTableLayout.column.journal * scale) - (journalTableLayout.margin.cx + journalTableLayout.margin.cx)) * layout.inchPerDot
    ) || []

    const rowHeight =
      journalTableLayout.margin.cy + 
      (lineHeight * scale * Math.max(1, Math.max(titleJpnLines.length + titleEngLines.length, journalLines.length))) +
      journalTableLayout.margin.cy

    if ((at + rowHeight) > bottom) {
      lastSlide.addTable(header.concat(body), {
        x: layout.margin, y: tableTop,
        colW: [
          journalTableLayout.column.number * scale,
          titleWidth,
          journalTableLayout.column.journal * scale,
          journalTableLayout.column.date * scale
        ],
        border: { pt: 1, color: colors.border }
      })
      continueText({ ...other, slide: lastSlide, top: at })

      lastSlide = pptx.addSlide({ masterName: "MASTER_SLIDE" })
      tableTop = layout.margin
      body = []
      at = tableTop + ((journalTableLayout.margin.cy + (lineHeight * scale) + journalTableLayout.margin.cy) * header.length)
    }
    const background = ((i % 2) === 0) ? colors.even : colors.odd
    
    body.push([{
      text: `${i + 1}`,
      options: {
        ...layout.cellOption,
        fontSize: (layout.cellOption.fontSize ?? 0) * scale,
        fill: { color: background },
        align: 'center'
      }
    }, titleJpnLines.length === 0 ? {
      text: titleEngLines.join('\n'),
      options: {
        ...layout.cellOption,
        hyperlink: article.url ? { url: article.url } : undefined,
        fontSize: (layout.cellOption.fontSize ?? 0) * scale,
        breakLine: true,
        fill: { color: background }
      }
    } : titleEngLines.length === 0 ? {
      text: titleJpnLines.join('\n'),
      options: {
        ...layout.cellOption,
        hyperlink: article.url ? { url: article.url } : undefined,
        fontSize: (layout.cellOption.fontSize ?? 0) * scale,
        breakLine: true,
        fill: { color: background }
      }
    } : {
      text: [{
        text: titleJpnLines.join('\n'),
        options: {
          ...layout.cellOption,
          hyperlink: article.url ? { url: article.url } : undefined,
          fontSize: (layout.cellOption.fontSize ?? 0) * scale,
          breakLine: true
        }
      }, {
        text: titleEngLines.join('\n'),
        options: {
          ...layout.cellOption,
          hyperlink: article.url ? { url: article.url } : undefined,
          fontSize: (layout.cellOption.fontSize ?? 0) * scale,
          color: '808080',
          breakLine: true
        }
      }],
      options: {
        fill: { color: background }
      }
    }, {
      text: journalLines.join('\n'),
      options: {
        ...layout.cellOption,
        fontSize: (layout.cellOption.fontSize ?? 0) * scale,
        fill: { color: background },
        breakLine: true
      }
    }, {
      text: getJournalDate(article),
      options: {
        ...layout.cellOption,
        fontSize: (layout.cellOption.fontSize ?? 0) * scale,
        fill: { color: background },
        align: 'center'
      }
    }])
    at += rowHeight
    i++
  }
  if (body.length > 0) {
    lastSlide.addTable(header.concat(body), {
      x: layout.margin, y: tableTop,
      colW: [
        journalTableLayout.column.number * scale,
        titleWidth, 
        journalTableLayout.column.journal * scale,
        journalTableLayout.column.date * scale
      ],
      border: { pt: 1, color: colors.border }
    })
  }
  return {
    slide: lastSlide,
    bottom: at
  }
}
